import { Box, Typography, Chip, LinearProgress, Alert } from '@mui/material'
import {
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Route as RouteIcon,
  Update as UpdateIcon,
  SocialDistance as DistanceIcon,
  AccessTime as TimeIcon,
  Timer as TimerIcon,
  HourglassEmpty as HourglassIcon
} from '@mui/icons-material'
import { LoadDetails } from '@/types/load'
import { isEmptyCheck } from '@/utils/isEmptyCheck'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import relativeTime from 'dayjs/plugin/relativeTime'

// Extend dayjs with plugins
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)

interface TrackingStatusProps {
  load: LoadDetails
}

/**
 * Get timezone abbreviation (e.g., CST, EST) at the timestamp's own date,
 * so a summer time is labelled CDT even when viewed in winter.
 * With no tz it uses the viewer's zone, like the web app's getTzName.
 */
const getTzAbbr = (timestamp?: number | string, tz?: string): string => {
  if (!timestamp) return ''
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short'
    })
    const parts = formatter.formatToParts(dayjs(timestamp).toDate())
    return parts.find(part => part.type === 'timeZoneName')?.value || ''
  } catch {
    return ''
  }
}

/**
 * Format check call date/time with timezone (matching main app)
 */
const formatCheckCallDateTime = (
  timestamp?: number | string,
  tz?: string
): string => {
  if (!timestamp) return ''
  try {
    const parsed = tz ? dayjs(timestamp).tz(tz) : dayjs(timestamp)
    if (!parsed.isValid()) return ''
    return parsed.format('ddd, MMM DD, hh:mm A')
  } catch {
    return ''
  }
}

/**
 * Format ETA date/time with timezone
 * Main app: CheckCallMessage prints the ETA, then its zone in brackets
 */
const formatEtaDateTime = (timestamp?: number, tz?: string): string => {
  if (!timestamp) return 'Not Available'
  try {
    const parsed = tz ? dayjs(timestamp).tz(tz) : dayjs(timestamp)
    if (!parsed.isValid()) return 'Not Available'
    const dateTime = `${parsed.format('ddd, MMM DD')}, ${parsed.format('hh:mm A')}`
    const tzAbbr = getTzAbbr(timestamp, tz)
    return tzAbbr ? `${dateTime} (${tzAbbr})` : dateTime
  } catch {
    return 'Not Available'
  }
}

/**
 * Get status color and label from delivery status
 */
const getStatusInfo = (deliveryStatus?: {
  status?: string
  statusColor?: string
}): {
  color: string
  label: string
} => {
  let color = '#757575'
  let label = 'Unknown'

  if (deliveryStatus?.statusColor) {
    switch (deliveryStatus.statusColor.toLowerCase()) {
      case 'green':
        color = '#4caf50'
        label = 'On Time'
        break
      case 'orange':
        color = '#ff9800'
        label = 'At Risk'
        break
      case 'red':
        color = '#f44336'
        label = 'Delayed'
        break
    }
  } else if (deliveryStatus?.status) {
    const status = deliveryStatus.status.toLowerCase()
    if (status.includes('on time') || status.includes('ontime')) {
      color = '#4caf50'
      label = 'On Time'
    } else if (status.includes('at risk') || status.includes('atrisk')) {
      color = '#ff9800'
      label = 'At Risk'
    } else if (status.includes('delay')) {
      color = '#f44336'
      label = 'Delayed'
    }
  }

  return { color, label }
}

/**
 * Get progress bar color based on percentage
 */
const getProgressBarColor = (percentage: number): string => {
  if (percentage >= 80) return '#4caf50'
  if (percentage >= 50) return '#ff9800'
  return '#f44336'
}

/**
 * Tracking Status Component
 * Displays last check call location and ETA information (matching main app CheckCallMessage UI)
 */
const TrackingStatus = ({ load }: TrackingStatusProps) => {
  const checkCall = load.checkCall
  const lastCheckCall = checkCall?.lastCheckCall
  const calculatedData = lastCheckCall?.calculated_data
  const etaData = checkCall?.checkoutEtaData || calculatedData

  // If no tracking data, don't render
  if (isEmptyCheck(lastCheckCall) && isEmptyCheck(etaData)) {
    return null
  }

  // Get delivery status info
  const deliveryStatus = calculatedData?.deliveryStatus
  const statusInfo = getStatusInfo(deliveryStatus)

  // Location and time
  const rawLocation = [
    calculatedData?.currentLocation,
    lastCheckCall?.current_address,
    etaData?.currentLocation
  ].find(location => !isEmptyCheck(location))
  const currentLocation = rawLocation || 'Unknown location'
  const currentTimezone = calculatedData?.timezones?.currentAddressTimezone
  const currentTime = lastCheckCall?.current_time || etaData?.current_time
  const formattedTime = formatCheckCallDateTime(currentTime, currentTimezone)
  const tzAbbr = getTzAbbr(currentTime, currentTimezone)

  // ETA info
  const etaTimestamp =
    calculatedData?.etaToDestinationTimestamp ||
    etaData?.etaToDestinationTimestamp
  const etaTimezone =
    calculatedData?.etaToDestinationTimezone ||
    etaData?.etaToDestinationTimezone
  const etaText = formatEtaDateTime(etaTimestamp, etaTimezone)

  // Progress
  const progressPercentage = Math.min(
    Math.max(calculatedData?.truckPositionOnRoute?.percentageCompleted || 0, 0),
    100
  )

  // Distance metrics
  const distanceMetrics =
    calculatedData?.distanceMetrics || etaData?.distanceMetrics
  const travelledSoFar = distanceMetrics?.travelledSoFar || 0
  const travelledSinceLastUpdate =
    distanceMetrics?.travelledSinceLastUpdate || 0
  const milesLeft = distanceMetrics?.milesLeft || 0

  // Time metrics
  const timeMetrics = calculatedData?.timeMetrics || etaData?.timeMetrics
  const timeUtilizedSoFar = timeMetrics?.timeUtilizedSoFar || '—'
  const timeUsedSinceLastUpdate = timeMetrics?.timeUsedSinceLastUpdate || '—'
  const timeRemaining = timeMetrics?.timeRemaining || '—'

  // Additional info
  const totalDistance =
    calculatedData?.totalDistance || etaData?.totalDistance || 0
  const isTeam = calculatedData?.isTeam
  const breakTime = calculatedData?.breakTime || 30

  // Check if invalid/outdated
  const isInvalid = lastCheckCall?.invalid

  // Check if we have any meaningful data to show (before the 'Unknown location' fallback)
  if (!rawLocation && isEmptyCheck(etaTimestamp)) {
    return null
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Trip Summary Header */}
      <Box
        sx={{
          p: 2,
          border: '2px solid',
          borderColor: isInvalid ? '#fecaca' : '#bfdbfe',
          borderRadius: 2,
          bgcolor: isInvalid
            ? 'rgba(254, 202, 202, 0.3)'
            : 'rgba(191, 219, 254, 0.3)'
        }}
      >
        {/* Header with status chip */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography sx={{ fontSize: '1.1rem' }}>🚛</Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: isInvalid ? '#b91c1c' : 'primary.main'
            }}
          >
            Trip Summary
          </Typography>
          {statusInfo.label !== 'Unknown' && (
            <Chip
              size="small"
              label={statusInfo.label}
              sx={{
                bgcolor: statusInfo.color,
                color: 'white',
                fontWeight: 700,
                fontSize: '11px',
                ml: 'auto'
              }}
            />
          )}
          {isInvalid && (
            <Chip
              size="small"
              label="INVALID"
              sx={{
                bgcolor: '#f44336',
                color: 'white',
                fontWeight: 700,
                fontSize: '10px'
              }}
            />
          )}
        </Box>

        {/* Current Location and Time Box */}
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'rgba(255,255,255,0.6)',
            border: '1px solid',
            borderColor: isInvalid ? '#fecaca' : '#bfdbfe'
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}
          >
            <LocationIcon
              sx={{ fontSize: 16, color: isInvalid ? '#dc2626' : '#2196f3' }}
            />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Current Location: {currentLocation}
            </Typography>
          </Box>
          {formattedTime && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <ScheduleIcon sx={{ fontSize: 14, color: '#666' }} />
              <Typography
                variant="body2"
                sx={{ fontSize: '12px', color: '#666' }}
              >
                Current Time: {formattedTime} {tzAbbr && <em>({tzAbbr})</em>}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Trip Progress Bar */}
        {progressPercentage > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Trip Progress: {progressPercentage.toFixed(0)}% Complete
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  bgcolor: getProgressBarColor(progressPercentage),
                  borderRadius: 5
                }
              }}
            />
          </Box>
        )}

        {/* Distance Metrics */}
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'rgba(255,255,255,0.6)',
            border: '1px solid',
            borderColor: isInvalid ? '#fecaca' : '#bbf7d0'
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: isInvalid ? '#b91c1c' : '#15803d'
            }}
          >
            Distance Metrics
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1
            }}
          >
            {/* Labels */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <RouteIcon sx={{ fontSize: 14, color: '#4caf50' }} />
              <Typography
                variant="body2"
                sx={{ fontSize: '11px', color: '#666', fontWeight: 500 }}
              >
                Total Traveled
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <UpdateIcon sx={{ fontSize: 14, color: '#ff9800' }} />
              <Typography
                variant="body2"
                sx={{ fontSize: '11px', color: '#666', fontWeight: 500 }}
              >
                Since Last Update
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DistanceIcon sx={{ fontSize: 14, color: '#f44336' }} />
              <Typography
                variant="body2"
                sx={{ fontSize: '11px', color: '#666', fontWeight: 500 }}
              >
                Distance Remaining
              </Typography>
            </Box>
            {/* Values */}
            <Typography
              variant="body2"
              sx={{ fontSize: '12px', fontWeight: 600 }}
            >
              {travelledSoFar || '—'} mi
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '12px', fontWeight: 600 }}
            >
              {travelledSinceLastUpdate || '—'} mi
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '12px', fontWeight: 600 }}
            >
              {milesLeft || '—'} mi
            </Typography>
          </Box>
        </Box>

        {/* Time Analysis */}
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'rgba(255,255,255,0.6)',
            border: '1px solid',
            borderColor: isInvalid ? '#fecaca' : '#e9d5ff'
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: isInvalid ? '#b91c1c' : '#7c3aed'
            }}
          >
            Time Analysis
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1
            }}
          >
            {/* Labels */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TimeIcon sx={{ fontSize: 14, color: '#9c27b0' }} />
              <Typography
                variant="body2"
                sx={{ fontSize: '11px', color: '#666', fontWeight: 500 }}
              >
                Since Start
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TimerIcon sx={{ fontSize: 14, color: '#673ab7' }} />
              <Typography
                variant="body2"
                sx={{ fontSize: '11px', color: '#666', fontWeight: 500 }}
              >
                Since Last Call
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <HourglassIcon sx={{ fontSize: 14, color: '#3f51b5' }} />
              <Typography
                variant="body2"
                sx={{ fontSize: '11px', color: '#666', fontWeight: 500 }}
              >
                Time Remaining
              </Typography>
            </Box>
            {/* Values */}
            <Typography
              variant="body2"
              sx={{ fontSize: '12px', fontWeight: 600 }}
            >
              {timeUtilizedSoFar}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '12px', fontWeight: 600 }}
            >
              {timeUsedSinceLastUpdate}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '12px', fontWeight: 600 }}
            >
              {timeRemaining}
            </Typography>
          </Box>
        </Box>

        {/* ETA Information Alert */}
        {etaTimestamp && (
          <Alert
            severity={isInvalid ? 'warning' : 'info'}
            sx={{
              py: 0.5,
              px: 1.5,
              bgcolor: isInvalid ? '#fff3cd' : '#f3f8ff',
              border: '1px solid',
              borderColor: isInvalid ? '#ffeaa7' : '#c5d9ff',
              color: isInvalid ? '#856404' : '#1565c0',
              '& .MuiAlert-icon': {
                color: isInvalid ? '#856404' : '#1976d2'
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, fontSize: '12px' }}
            >
              <strong>ETA:</strong> {etaText} {isInvalid && '(Invalid Data)'}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '11px', mt: 0.25 }}>
              <strong>Mode:</strong> {isTeam ? 'Team' : 'Solo'} |{' '}
              <strong>Break:</strong> {breakTime} min |{' '}
              <strong>Total Distance:</strong> {totalDistance || '—'} mi
            </Typography>
          </Alert>
        )}
      </Box>
    </Box>
  )
}

export default TrackingStatus
